import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  type ReadinessDto,
  type RunSimResultDto,
  type SavedSimSummaryDto,
  fetchReadiness,
  fetchSavedSimDetail,
  fetchSavedSims,
  runSimulation,
  saveSimulation,
} from "../api";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";

function FanChart({ bands, height = 260 }: { bands: RunSimResultDto["bands"]; height?: number }) {
  const w = 640;
  const pad = 30;
  const days = bands.days;
  const p10 = bands.percentiles["10"], p25 = bands.percentiles["25"];
  const p50 = bands.percentiles["50"], p75 = bands.percentiles["75"], p90 = bands.percentiles["90"];
  const all = [...p10, ...p90];
  const mn = Math.min(...all) * 0.98, mx = Math.max(...all) * 1.02;
  const X = (i: number) => pad + (i / (days.length - 1)) * (w - 2 * pad);
  const Y = (v: number) => height - pad - ((v - mn) / (mx - mn)) * (height - 2 * pad);
  const poly = (arr: number[]) => arr.map((v, i) => `${X(i).toFixed(1)},${Y(v).toFixed(1)}`);
  const outer = [...poly(p90), ...poly(p10).reverse()].join(" ");
  const inner = [...poly(p75), ...poly(p25).reverse()].join(" ");
  const median = poly(p50).join(" ");
  const s0 = p50[0];
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="xMidYMid meet">
      <polygon points={outer} fill="var(--ink-faint)" opacity={0.15} />
      <polygon points={inner} fill="var(--accent-soft)" opacity={0.85} />
      <line x1={pad} y1={Y(s0)} x2={w - pad} y2={Y(s0)} stroke="var(--ink-faint)" strokeDasharray="3 3" opacity={0.5} />
      <text x={pad} y={Y(s0) - 4} fontFamily="var(--font-mono)" fontSize={9} fill="var(--ink-faint)">t=0</text>
      <polyline points={median} fill="none" stroke="var(--accent)" strokeWidth={2} />
      <circle cx={X(0)} cy={Y(s0)} r={3.5} fill="var(--ink)" />
      <text x={w - pad} y={Y(p50[p50.length - 1]) - 6} textAnchor="end" fontFamily="var(--font-mono)" fontSize={10} fontWeight={700} fill="var(--accent)">
        {(p50[p50.length - 1] - s0).toFixed(1)}%
      </text>
    </svg>
  );
}

function ReadinessTable({ r }: { r: ReadinessDto }) {
  return (
    <table className="stocks-table">
      <thead><tr><th>Role</th><th className="l">Source</th><th>State</th><th className="r">As-of</th></tr></thead>
      <tbody>
        <tr>
          <td style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>DRIFT</td>
          <td className="l" style={{ fontSize: "0.8rem" }}>
            {r.drift.available
              ? `§10.13 return coverage blend (${Object.keys(r.drift.contributing_segments).join(" + ")}, h=${r.drift.matched_horizon})`
              : "no coverage-resolved return signal"}
          </td>
          <td>{r.drift.available
            ? <span className="chip st-champion">FORECASTED</span>
            : <span className="chip st-challenger">NO SIGNAL</span>}</td>
          <td className="r mono" style={{ fontSize: "0.76rem" }}>{r.drift.available ? `n=${r.drift.n_resolved_errors}` : "—"}</td>
        </tr>
        <tr>
          <td style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>VOL</td>
          <td className="l" style={{ fontSize: "0.8rem" }}>
            {r.vol.available
              ? `stored realized-vol persistence — vol_${r.vol.window}d (${(r.vol.sigma_annual! * 100).toFixed(1)}% ann.)`
              : "no realized-vol feature"} <FieldHelp term="real_magnitude_sigma" />
          </td>
          <td>{r.vol.available
            ? <span className="chip st-champion">FORECASTED</span>
            : <span className="chip st-challenger">NO DATA</span>}</td>
          <td className="r mono" style={{ fontSize: "0.76rem" }}>{r.vol.as_of_date ?? "—"}</td>
        </tr>
        <tr>
          <td style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>EVENT</td>
          <td className="l" style={{ fontSize: "0.8rem" }}>{r.event.reason} <FieldHelp term="event_impact_parked" /></td>
          <td><span className="gaps-badge">FALLBACK — excluded</span></td>
          <td className="r">—</td>
        </tr>
      </tbody>
    </table>
  );
}

export function SimulationWorkbenchPage({
  token, securityId, isAdmin, onUnauthorized, onBack,
}: {
  token: string; securityId: string; isAdmin: boolean; onUnauthorized: () => void; onBack: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  function authAware(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) { onUnauthorized(); return true; }
    return false;
  }

  const [horizon, setHorizon] = useState(20);
  const [volWindowOverride, setVolWindowOverride] = useState<number | null>(null);
  const [nPaths, setNPaths] = useState(2000);
  const [distribution, setDistribution] = useState<"normal" | "t5">("normal");
  const [seed, setSeed] = useState<string>("");
  const [readiness, setReadiness] = useState<ReadinessDto | null>(null);
  const [result, setResult] = useState<RunSimResultDto | null>(null);
  const [running, setRunning] = useState(false);
  const [savedSims, setSavedSims] = useState<SavedSimSummaryDto[] | null>(null);
  const [simName, setSimName] = useState(`${securityId} — ${horizon}d base case`);
  const [saveOut, setSaveOut] = useState<string | null>(null);
  const [viewDetail, setViewDetail] = useState<(RunSimResultDto & { sim_id: string }) | null>(null);

  useEffect(() => {
    fetchSavedSims(token, securityId).then(setSavedSims).catch((e) => { if (!authAware(e)) setError(String(e)); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, securityId]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchReadiness(token, securityId, horizon, volWindowOverride ?? undefined)
      .then(setReadiness)
      .catch((e) => { if (!authAware(e)) setError(e instanceof ApiError ? e.message : "readiness failed"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, securityId, horizon, volWindowOverride, isAdmin]);

  async function runSim() {
    setRunning(true);
    setError(null);
    try {
      const r = await runSimulation(token, {
        security_id: securityId, horizon_days: horizon, n_paths: nPaths, distribution,
        seed: seed ? Number(seed) : null, vol_window_override: volWindowOverride,
      });
      setResult(r);
    } catch (e) {
      if (!authAware(e)) setError(e instanceof ApiError ? e.message : "simulation failed");
    } finally {
      setRunning(false);
    }
  }

  async function save() {
    if (!result) return;
    try {
      const res = await saveSimulation(token, {
        security_id: securityId, name: simName,
        component_models: { drift: result.inputs.drift, vol: result.inputs.vol, event: result.inputs.event },
        reference_betas: {}, composition: "independent",
        mc_params: { n_paths: nPaths, distribution, seed: seed ? Number(seed) : null },
        horizon, provenance: { caveats: result.caveats, vol_regime: result.vol_regime },
      });
      setSaveOut(res.sim_id);
      fetchSavedSims(token, securityId).then(setSavedSims).catch(() => undefined);
    } catch (e) {
      if (!authAware(e)) setError(e instanceof ApiError ? e.message : "save failed");
    }
  }

  async function loadSaved(simId: string) {
    try {
      const d = await fetchSavedSimDetail(token, simId);
      setViewDetail(d);
    } catch (e) {
      if (!authAware(e)) setError(e instanceof ApiError ? e.message : "load failed");
    }
  }

  const caveatBanners = useMemo(() => {
    const r = result ?? viewDetail;
    if (!r) return null;
    return (
      <>
        {r.caveats.map((c, i) => (
          <div key={i} className={`banner show ${c.severity === "warning" ? "" : ""}`} style={{ display: "flex", marginTop: 8, background: c.severity === "warning" ? "var(--down-soft)" : undefined }}>
            <span>{c.severity === "warning" ? "⚠" : "ⓘ"}</span>
            <span>{c.message}</span>
          </div>
        ))}
      </>
    );
  }, [result, viewDetail]);

  if (!isAdmin) {
    return (
      <>
        <button className="btn ghost sm" onClick={onBack} style={{ marginBottom: 10 }}>← back</button>
        <div className="page-head">
          <div>
            <h1>Simulation — {securityId}</h1>
            <div className="page-sub">Read-only. Saved recipes are admin-built; you can view the forecast cone and its provenance.</div>
          </div>
        </div>
        <PageHelper>
          A saved Monte Carlo recipe for this stock, re-run at today's close. <FieldHelp term="mc_reliable_spread" />
          The shaded band is a real, reliable measure of spread; the center line is a best guess that carries real
          uncertainty of its own — never read the median as a promise.
        </PageHelper>
        {error && <div className="error">{error}</div>}
        {!savedSims ? (
          <div className="empty-state">Loading…</div>
        ) : savedSims.length === 0 ? (
          <div className="empty-state">
            <div className="headline">No saved simulations for this stock yet</div>
            <div>An admin builds these in the Simulation Workbench.</div>
          </div>
        ) : (
          <div className="card" style={{ padding: 16 }}>
            {!viewDetail ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {savedSims.map((s) => (
                  <div key={s.sim_id} onClick={() => loadSaved(s.sim_id)}
                    style={{ border: "1px solid var(--rule)", borderRadius: 8, padding: "10px 14px", cursor: "pointer", display: "flex", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{s.name}</div>
                      <div className="note" style={{ fontSize: "0.76rem" }}>{s.horizon}d · {s.composition}</div>
                    </div>
                    <span className="note">{new Date(s.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <button className="btn ghost sm" onClick={() => setViewDetail(null)} style={{ marginBottom: 10 }}>← saved sims</button>
                <FanChart bands={viewDetail.bands} />
                <div className="legend" style={{ display: "flex", gap: 14, fontSize: "0.74rem", color: "var(--ink-faint)", marginTop: 6 }}>
                  <span>▮ median path</span><span style={{ color: "var(--accent)" }}>▨ 25–75%</span><span>▨ 10–90%</span>
                </div>
                {caveatBanners}
                <div className="hint" style={{ marginTop: 10, fontSize: "0.78rem", fontStyle: "italic", color: "var(--ink-faint)" }}>{viewDetail.honesty_note}</div>
              </>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <button className="btn ghost sm" onClick={onBack} style={{ marginBottom: 10 }}>← back</button>
      <div className="page-head">
        <div>
          <h1>Simulation Workbench — {securityId}</h1>
          <div className="page-sub">Compose an SDE for one instrument — real drift + real-magnitude vol, Monte Carlo paths. A deliberate what-if, never a stored production object.</div>
        </div>
      </div>

      <PageHelper>
        Composes a Monte Carlo simulation from real inputs: DRIFT is the coverage-resolved return blend from this
        stock's segment models; VOL is its stored real-magnitude realized-vol feature, never the fitted{" "}
        <FieldHelp term="rank_space_sigma" />rank-space σ (shown for reference only). <FieldHelp term="mc_drift_distribution" />
        Drift is drawn per-path from a distribution, not a single point — the fan's center is honestly uncertain.
      </PageHelper>

      {error && <div className="error" style={{ margin: "10px 0" }}>{error}</div>}

      <div className="card">
        <div className="h3"><span>Parameters</span></div>
        <div style={{ padding: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div className="fld"><label className="note" style={{ display: "block", marginBottom: 4 }}>Horizon (days)</label>
            <input className="txt" type="number" min={1} max={252} value={horizon} onChange={(e) => setHorizon(Number(e.target.value))} style={{ width: 90 }} /></div>
          <div className="fld"><label className="note" style={{ display: "block", marginBottom: 4 }}>Vol window override</label>
            <select value={volWindowOverride ?? ""} onChange={(e) => setVolWindowOverride(e.target.value ? Number(e.target.value) : null)}>
              <option value="">auto (horizon-matched)</option>
              <option value="10">vol_10d</option>
              <option value="20">vol_20d</option>
              <option value="60">vol_60d</option>
            </select></div>
          <div className="fld"><label className="note" style={{ display: "block", marginBottom: 4 }}>Paths</label>
            <input className="txt" type="number" min={100} max={200000} value={nPaths} onChange={(e) => setNPaths(Number(e.target.value))} style={{ width: 110 }} /></div>
          <div className="fld"><label className="note" style={{ display: "block", marginBottom: 4 }}>Distribution</label>
            <select value={distribution} onChange={(e) => setDistribution(e.target.value as "normal" | "t5")}>
              <option value="normal">Normal</option>
              <option value="t5">Student-t (df=5, fat tails)</option>
            </select></div>
          <div className="fld"><label className="note" style={{ display: "block", marginBottom: 4 }}>Seed</label>
            <input className="txt" value={seed} onChange={(e) => setSeed(e.target.value)} placeholder="random" style={{ width: 90 }} /></div>
        </div>
      </div>

      <div className="card">
        <div className="h3"><span>Readiness <span className="note">which components are forecasted vs fallback, before any path is drawn</span></span></div>
        <div style={{ padding: 16 }}>
          {!readiness ? <div className="note">loading…</div> : (
            <>
              <ReadinessTable r={readiness} />
              {readiness.vol_regime.status === "ELEVATED" && (
                <div className="banner show" style={{ display: "flex", marginTop: 10, background: "var(--down-soft)" }}>
                  <span>⚠</span><span>{readiness.vol_regime.note} <FieldHelp term="vol_regime_flag" /></span>
                </div>
              )}
              {readiness.vol.available && (
                <div className="hint" style={{ marginTop: 8 }}>
                  Fitted rank-space σ for reference only (never simulated): {readiness.vol.rank_space_sigma_reference?.toFixed(4) ?? "—"} <FieldHelp term="rank_space_sigma" />
                </div>
              )}
              <div style={{ marginTop: 14 }}>
                <button className="btn go" onClick={runSim} disabled={!readiness.ready || running}>
                  {running ? "running…" : "▶ Run simulation"}
                </button>
                {!readiness.ready && <span className="hint" style={{ marginLeft: 8 }}>drift and vol must both be available to run</span>}
              </div>
            </>
          )}
        </div>
      </div>

      {result && (
        <div className="card">
          <div className="h3"><span>Forecast cone <FieldHelp term="black_scholes_check" /> <span className="note">Monte Carlo percentile bands</span></span></div>
          <div style={{ padding: 16 }}>
            <FanChart bands={result.bands} />
            <div style={{ display: "flex", gap: 14, fontSize: "0.74rem", color: "var(--ink-faint)", marginTop: 6, flexWrap: "wrap" }}>
              <span>▮ median path</span><span style={{ color: "var(--accent)" }}>▨ 25–75%</span><span>▨ 10–90%</span>
              <span className="mono" style={{ marginLeft: "auto" }}>
                median {result.terminal_summary.median_return_pct.toFixed(1)}% · 10–90% [{result.terminal_summary.p10_return_pct.toFixed(1)}%, {result.terminal_summary.p90_return_pct.toFixed(1)}%]
              </span>
            </div>
            {caveatBanners}
            <div className="hint" style={{ marginTop: 10, fontSize: "0.78rem", fontStyle: "italic", color: "var(--ink-faint)" }}>
              <FieldHelp term="mc_reliable_spread" />{result.honesty_note}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
              <input className="txt" style={{ minWidth: 260 }} value={simName} onChange={(e) => setSimName(e.target.value)} />
              <button className="btn go" onClick={save}>✓ Save recipe</button>
              {saveOut && <span className="chip st-champion">✓ saved {saveOut}</span>}
            </div>
            <div className="hint" style={{ marginTop: 8 }}>
              Saves the recipe only (§6.3) — paths re-run daily at current close, cached per-day, never stored as a
              historical series.
            </div>
          </div>
        </div>
      )}

      {savedSims && savedSims.length > 0 && (
        <div className="card">
          <div className="h3"><span>Saved recipes for this stock</span></div>
          <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            {savedSims.map((s) => (
              <div key={s.sim_id} style={{ border: "1px solid var(--rule)", borderRadius: 8, padding: "10px 14px", display: "flex", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{s.name}</div>
                  <div className="note" style={{ fontSize: "0.76rem" }}>{s.horizon}d · {s.composition}</div>
                </div>
                <span className="note">{new Date(s.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
